import _ from 'lodash';
import { useMemo, useState } from 'react';

import useUiAlerts from '../stores/AlertsStore';
import { useDataService } from './ApiDataService';
import { useGenericError } from './FormErrorService';
import { useSuccessfulAction } from '../utilities/FormUtilities';

export function useFormDeleteWrapper(successMessage) {
    const [, uiAlertActions] = useUiAlerts();
    const handleGenericError = useGenericError();
    const handleSuccessfulDelete = useSuccessfulAction(successMessage);
    const [deleting, setDeleting] = useState(false);

    const deleteWrapper = (callerConfig) => {
        const emptyRefresherFunc = _.noop;
        const funcConfig = _.defaults(callerConfig, {
            postDeleteHandler: (response) =>
                handleSuccessfulDelete(
                    response,
                    callerConfig.onDeleteGoTo,
                    callerConfig.refresherFunc || emptyRefresherFunc
                ),
            errorHandler: handleGenericError,
        });

        return () => {
            return Promise.resolve(setDeleting(true))
                .then(() => uiAlertActions.handleRemove())
                .then(funcConfig.deleteFunction)
                .then(funcConfig.postDeleteHandler)
                .catch(funcConfig.errorHandler)
                .finally(setDeleting(false));
        };
    };

    return {
        deleting,
        deleteWrapper,
    };
}

export default function FormDeleteServiceFactory(dataService) {
    function deleteSelection(url, params, tableSelection, postDeleteHandler = (response) => response) {
        return dataService.post(url, tableSelection, params).then(postDeleteHandler);
    }

    function removeSelection(url, params, tableSelection, postDeleteHandler = (response) => response) {
        return dataService.remove(url, tableSelection, params).then(postDeleteHandler);
    }

    function deleteById(url, id, postDeleteHandler = (response) => response, isV3Api = false) {
        if (isV3Api) {
            return dataService.post(`${url}/${id}/delete`).then(postDeleteHandler);
        }

        return dataService.remove(`${url}/${id}`).then(postDeleteHandler);
    }

    return {
        deleteSelection,
        removeSelection,
        deleteById,
    };
}

export function useFormDeleteService() {
    const dataService = useDataService();
    return useMemo(() => FormDeleteServiceFactory(dataService), [dataService]);
}
